// Guard de refetch TV-first (princípio S5: NUNCA reordenar/remover o card que está
// SOB O FOCO — em TV isso rouba o controle remoto ou cria foco órfão).
//
// Lógica PURA (sem React, sem Norigin) extraída do handler de sync pós-pull em
// providers.tsx pra poder ser testada e reusada sem duplicar a regra de prefixos.
// O handler decide, a partir da focus-key atual, quais rails podem invalidar agora
// (livres) e quais precisam esperar o foco sair (adiadas).
//
// Cada rail mapeia uma query (`['scope', scope, ...parts]`) pra os prefixos das
// focus-keys dos seus cards. Uma rail pode ter VÁRIOS prefixos quando a MESMA query
// alimenta mais de uma aba/tela (ex: a query de "continuar assistindo" de Séries
// aparece na aba Descobrir com `car-cont-*` E na aba Minha Lista com `mylist-cont-*`).
// `focusPrefixes` vazio = rail nunca tem foco próprio → sempre livre pra invalidar.

export interface RefetchRail {
  /** Sufixo da queryKey após `['scope', scope, ...]`. */
  readonly parts: readonly string[]
  /** Prefixos das focus-keys dos cards desta rail. Vazio = sem foco próprio. */
  readonly focusPrefixes: readonly string[]
}

export interface RailPartition {
  /** Rails que podem invalidar agora (não estão sob o foco). */
  readonly free: RefetchRail[]
  /** Rails sob o foco — invalidação adiada até o foco sair. */
  readonly deferred: RefetchRail[]
}

/** True se a focus-key atual pertence a algum card desta rail. */
export function isRailFocused(rail: RefetchRail, focusedKey: unknown): boolean {
  if (typeof focusedKey !== 'string' || focusedKey.length === 0) return false
  for (const prefix of rail.focusPrefixes) {
    if (focusedKey.startsWith(prefix)) return true
  }
  return false
}

/**
 * Particiona as rails entre "livres" (invalidar já) e "adiadas" (sob o foco)
 * conforme a focus-key atual. Função pura: não toca React Query nem o foco.
 */
export function partitionRailsByFocus(
  rails: readonly RefetchRail[],
  focusedKey: unknown,
): RailPartition {
  const free: RefetchRail[] = []
  const deferred: RefetchRail[] = []
  for (const rail of rails) {
    if (isRailFocused(rail, focusedKey)) deferred.push(rail)
    else free.push(rail)
  }
  return { free, deferred }
}
